import { useEffect, useState } from 'react';
import {
  onSnapshot,
  query,
  where,
  orderBy,
  type Query,
  type DocumentReference,
  type DocumentData,
  type QuerySnapshot,
} from 'firebase/firestore';

export { query, where, orderBy };

/**
 * Subscribes to a Firestore query and returns a typed array of documents
 * with their IDs merged in.
 *
 * Pass a `queryKey` string that changes whenever the query parameters change
 * (e.g. a cycle ID, user ID). The hook re-subscribes whenever `queryKey`
 * changes, so callers that derive queries from async state must supply one.
 * Callers whose query is fully stable at mount time can omit it.
 */
export function useCollection<T extends DocumentData>(
  q: Query<T> | null,
  queryKey?: string,
): { data: (T & { id: string })[]; loading: boolean; error: Error | null } {
  const [data, setData] = useState<(T & { id: string })[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!q) {
      setData([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const unsub = onSnapshot(
      q,
      (snap: QuerySnapshot<T>) => {
        setData(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      (err) => {
        setError(err);
        setLoading(false);
      },
    );

    return unsub;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryKey ?? '___stable___']);

  return { data, loading, error };
}

/**
 * Subscribes to a single Firestore document and returns the typed value
 * with its ID merged in, or null if it doesn't exist.
 */
export function useDocument<T extends DocumentData>(
  ref: DocumentReference<T> | null,
): { data: (T & { id: string }) | null; loading: boolean; error: Error | null } {
  const [data, setData] = useState<(T & { id: string }) | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!ref) {
      setLoading(false);
      return;
    }

    const unsub = onSnapshot(
      ref,
      (snap) => {
        setData(snap.exists() ? { id: snap.id, ...snap.data() } : null);
        setLoading(false);
      },
      (err) => {
        setError(err);
        setLoading(false);
      },
    );

    return unsub;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { data, loading, error };
}
